import { Fragment } from 'react';
import {
   Box,
   Heading,
   Flex,
   Text,
   Accordion,
   AccordionItem,
   AccordionButton,
   AccordionIcon,
   AccordionPanel,
   TableContainer,
   Table,
   Thead,
   Tr,
   Th,
   Tbody,
   Td,
   Link,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretUp } from '@fortawesome/pro-regular-svg-icons/faCaretUp';
import { faCaretDown } from '@fortawesome/pro-regular-svg-icons/faCaretDown';
import { _js } from '@ifixit/localize';

type VoteDataType = {
   userProfileLink: string;
   userid: string;
   username: string;
   up: number;
   upPercent: number;
   down: number;
   downPercent: number;
};

export type VoteHistoryDataType = {
   username: string;
   votesByUser: VoteDataType[];
   votesByOthers: VoteDataType[];
   upVotes: number;
   downVotes: number;
};

export function VoteHistory({
   username,
   votesByUser,
   votesByOthers,
   upVotes,
   downVotes,
}: VoteHistoryDataType) {
   const showVotes = upVotes > 0 || downVotes > 0;
   const showHistory = votesByUser !== undefined || votesByOthers !== undefined;

   return (
      <Box display={{ base: 'none', lg: 'unset' }} w="full">
         {showVotes && <Votes upVotes={upVotes} downVotes={downVotes} />}
         {showHistory && (
            <History username={username} votesByUser={votesByUser} votesByOthers={votesByOthers} />
         )}
      </Box>
   );
}

const Votes = ({ upVotes, downVotes }: { upVotes: number; downVotes: number }) => (
   <Fragment>
      <Heading as="h3" color="black" fontSize="xl">
         {_js('Votes')}
      </Heading>
      <Box
         my={3}
         pl={3}
         sx={{
            '.fa': {
               fontSize: '40px',
            },
         }}
      >
         <Flex color="brand.500" align="center">
            <FontAwesomeIcon icon={faCaretUp} className="fa" />
            <Text ml="8px" fontSize="18px" color="black" fontWeight="bold">
               {upVotes}
            </Text>
         </Flex>
         <Flex color="brand.500" align="center">
            <FontAwesomeIcon icon={faCaretDown} className="fa" />
            <Text ml="8px" fontSize="18px" color="black" fontWeight="bold">
               {downVotes}
            </Text>
         </Flex>
      </Box>
   </Fragment>
);

const History = ({
   username,
   votesByUser,
   votesByOthers,
}: {
   username: string;
   votesByUser: VoteDataType[];
   votesByOthers: VoteDataType[];
}) => (
   <Accordion allowToggle allowMultiple w="100%">
      <VoteAccordionItem votesBy={username} votes={votesByUser} />
      <VoteAccordionItem votesBy={_js('Others')} votes={votesByOthers} />
   </Accordion>
);

const VoteAccordionItem = ({ votesBy, votes }: { votesBy: string; votes: VoteDataType[] }) => (
   <AccordionItem border="none">
      <Heading as="h3">
         <AccordionButton bgColor="gray.100" borderRadius="lg">
            <Box flex="1" textAlign="left">
               {_js('Votes By ') + votesBy}
            </Box>
            <AccordionIcon />
         </AccordionButton>
      </Heading>
      <AccordionPanel p={0}>
         {votes.length > 0 ? <VoteTable voteData={votes} /> : <Text>{_js('No Votes')}</Text>}
      </AccordionPanel>
   </AccordionItem>
);

const VoteTable = ({ voteData }: { voteData: VoteDataType[] }) => (
   <TableContainer maxW="300px">
      <Table variant="simple" size="sm">
         <Thead>
            <Tr>
               <Th>{_js('Name')}</Th>
               <Th colSpan={2} px={1}>
                  {_js('Up')}
               </Th>
               <Th colSpan={2} px={1}>
                  {_js('Down')}
               </Th>
            </Tr>
         </Thead>
         <Tbody>
            <VoteRows voteData={voteData} />
         </Tbody>
      </Table>
   </TableContainer>
);

const VoteRows = ({ voteData }: { voteData: VoteDataType[] }) => (
   <>
      {voteData.map((voteInfo: VoteDataType, i: any) => (
         <VoteTableRow key={voteInfo.userid + i} {...voteInfo} />
      ))}
   </>
);

const VoteTableRow = ({
   userProfileLink,
   userid,
   username,
   up,
   upPercent,
   down,
   downPercent,
}: VoteDataType) => (
   <Tr>
      <Td>
         <Link href={userProfileLink} title={userid}>
            {username}
         </Link>
      </Td>
      <Td px={1}>{up}</Td>
      <Td px={1}>{upPercent}%</Td>
      <Td px={1}>{down}</Td>
      <Td px={1}>{downPercent}%</Td>
   </Tr>
);
